/**
 * Timezone-safe handling for calendar dates (no time component).
 *
 * JavaScript parses a date-only string like "2026-03-05" as UTC midnight, but
 * `getFullYear()/getMonth()/getDate()` read back in LOCAL time. Anywhere west of
 * UTC that lands on the previous day, which is why users in the Americas had to
 * pick one day later than users in East Africa to get the same travel range.
 *
 * These helpers keep a calendar date meaning the same day in every timezone.
 */

/**
 * Parse a value into a LOCAL-midnight Date.
 * Date-only strings are built component-wise so no UTC shift is applied.
 * Full timestamps (with a time part) are left to the normal parser.
 *
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
export const parseLocalDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).trim());
  if (match) {
    const [, y, m, d] = match;
    return new Date(Number(y), Number(m) - 1, Number(d));
  }
  return new Date(value);
};

/**
 * Format a date as the YYYY-MM-DD an <input type="date"> expects, using local
 * components so it round-trips with parseLocalDate.
 *
 * @param {string|Date|null|undefined} value
 * @returns {string}
 */
export const formatLocalDate = (value) => {
  const d = parseLocalDate(value);
  if (!d || Number.isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Render an INSTANT in the reader's own timezone, naming the zone.
 *
 * The counterpart to everything else here: those helpers keep a calendar DATE
 * meaning the same day everywhere, whereas this one takes a moment that is the
 * same for everybody and shows each reader when it falls for them. Used for
 * "your access ends ...", which teams in different zones must not read
 * differently - and where a bare date would be actively misleading, since the
 * cut-off is a time of day, not a day.
 *
 * @param {string|Date|null|undefined} value  an ISO instant
 * @returns {string} e.g. "Sep 17, 2026, 3:00 AM EAT", or '' if unparseable
 */
export const formatInstant = (value) => {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: 'numeric', minute: '2-digit',
    timeZoneName: 'short',
  });
};

/**
 * The instant a calendar day ENDS in the reader's own timezone, as ISO.
 *
 * Used when capturing an access window from a date the user picked: "cover me
 * until the 17th" means through the whole of their 17th. Two things this gets
 * right that a bare "2026-09-17" does not:
 *
 *  - The server would parse that string as midnight UTC, a different moment for
 *    every picker - 5.5 hours early for an auditor in India.
 *  - Midnight is the START of the named day, so cover expired as the day began
 *    and the named day itself was uncovered.
 *
 * Erring toward overlap is deliberate. Extra hours of cover are harmless; a gap
 * leaves a report with nobody authorised to review it.
 *
 * @param {string} dateStr  YYYY-MM-DD
 * @returns {string|undefined} ISO instant
 */
export const localDayEndISO = (dateStr) => {
  const d = parseLocalDate(dateStr);
  if (!d || Number.isNaN(d.getTime())) return undefined;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).toISOString();
};

/** Strip the time component, returning local midnight of the same calendar day. */
export const startOfDay = (value) => {
  const d = parseLocalDate(value);
  if (!d || Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

/**
 * Inclusive day count between two calendar dates (5th→12th = 8 days).
 * Uses local midnights, so DST transitions can't shave or add a day.
 */
export const inclusiveDayCount = (start, end) => {
  const s = startOfDay(start);
  const e = startOfDay(end);
  if (!s || !e) return 0;
  return Math.round((e - s) / 86400000) + 1;
};


/**
 * Is this a complete YYYY-MM-DD value?
 *
 * <input type="date"> reports an EMPTY value while a date is partially typed.
 * Feeding that straight back into state wipes the field mid-keystroke, which is
 * why the 4-digit year could never be typed (day/month complete fast enough to
 * survive). Use this to ignore incomplete input and only commit real dates.
 */
export const isCompleteDateString = (value) => {
  const str = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(str)) return false;

  // Typing a year digit-by-digit produces technically valid dates along the way
  // (0002, 0020, 0202 ...). Committing those re-renders the field and fights the
  // user's keystrokes - which is why the year appeared stuck in the 0000s/1900s.
  // Require a plausible year so only the finished value is accepted.
  const year = Number(str.slice(0, 4));
  return year >= 1900 && year <= 2200;
};

/**
 * onChange handler for a date input backed by a Date in state.
 * Commits only complete dates; ignores the transient empty value produced while
 * typing. Pair with `commitDateInputBlur` so the field can still be cleared.
 */
export const handleDateInputChange = (value, setter) => {
  if (isCompleteDateString(value)) setter(parseLocalDate(value));
};

/** onBlur companion: an empty field once focus leaves really means "cleared". */
export const commitDateInputBlur = (value, setter) => {
  if (!String(value || '').trim()) setter(null);
};

const dateOnly = {
  parseLocalDate, formatLocalDate, startOfDay, inclusiveDayCount,
  isCompleteDateString, handleDateInputChange, commitDateInputBlur,
};
export default dateOnly;
